import type {
  SapSyncJob,
  SapDlqEntry,
  SapPullWatermark,
  CircuitBreakerStatus,
  SapDashboardStatus,
} from '../domain/types';

function normalizeEntity<T>(raw: unknown): T {
  const obj = raw as Record<string, unknown>;
  if (obj?.attributes && obj?.id) {
    return { id: obj.id, ...obj.attributes } as T;
  }
  return obj as T;
}

export function transformSyncJobDtoToDomain(raw: unknown): SapSyncJob {
  const dto = normalizeEntity<Record<string, unknown>>(raw);
  return {
    id: Number(dto.id),
    documentId: String(dto.documentId || ''),
    direction: dto.direction as SapSyncJob['direction'],
    entityType: dto.entityType as SapSyncJob['entityType'],
    entityId: String(dto.entityId || ''),
    sapOperation: (dto.sapOperation as SapSyncJob['sapOperation']) ?? null,
    eventId: dto.eventId ? String(dto.eventId) : null,
    status: (dto.jobStatus || dto.status) as SapSyncJob['status'],
    httpMethod: (dto.httpMethod as SapSyncJob['httpMethod']) ?? null,
    url: dto.url ? String(dto.url) : null,
    requestHeaders: (dto.requestHeaders as Record<string, string>) ?? null,
    requestPayload: dto.requestPayload ?? null,
    responseStatus: dto.responseStatus != null ? Number(dto.responseStatus) : null,
    responseHeaders: (dto.responseHeaders as Record<string, string>) ?? null,
    responsePayload: dto.responsePayload ?? null,
    durationMs: dto.durationMs != null ? Number(dto.durationMs) : null,
    sapDocumentNumber: dto.sapDocumentNumber ? String(dto.sapDocumentNumber) : null,
    sapDocumentType: dto.sapDocumentType ? String(dto.sapDocumentType) : null,
    attempts: Number(dto.attempts || 0),
    lastAttemptAt: dto.lastAttemptAt ? String(dto.lastAttemptAt) : null,
    errorMessage: dto.errorMessage ? String(dto.errorMessage) : null,
    errorCode: dto.errorCode ? String(dto.errorCode) : null,
    correlationId: dto.correlationId ? String(dto.correlationId) : null,
    tenantId: dto.tenantId ? String(dto.tenantId) : null,
    completedAt: dto.completedAt ? String(dto.completedAt) : null,
    createdAt: String(dto.createdAt || ''),
    updatedAt: String(dto.updatedAt || ''),
  };
}

export function transformDlqEntryDtoToDomain(raw: unknown): SapDlqEntry {
  const dto = normalizeEntity<Record<string, unknown>>(raw);
  return {
    id: Number(dto.id),
    documentId: String(dto.documentId || ''),
    syncJob: dto.syncJob ? transformSyncJobDtoToDomain(dto.syncJob) : null,
    entityType: String(dto.entityType || ''),
    entityId: String(dto.entityId || ''),
    eventPayload: dto.eventPayload ?? null,
    errorMessage: String(dto.errorMessage || ''),
    errorCode: dto.errorCode ? String(dto.errorCode) : null,
    attempts: Number(dto.attempts || 0),
    lastAttemptAt: dto.lastAttemptAt ? String(dto.lastAttemptAt) : null,
    resolvedAt: dto.resolvedAt ? String(dto.resolvedAt) : null,
    resolvedBy: dto.resolvedBy ? String(dto.resolvedBy) : null,
    createdAt: String(dto.createdAt || ''),
    updatedAt: String(dto.updatedAt || ''),
  };
}

export function transformDashboardStatusToDomain(raw: unknown): SapDashboardStatus {
  const dto = raw as Record<string, unknown>;
  const watermarksRaw = Array.isArray(dto.pullWatermarks) ? dto.pullWatermarks : [];

  return {
    circuitBreaker: dto.circuitBreaker as CircuitBreakerStatus,
    syncJobStats: (dto.syncJobStats || {}) as SapDashboardStatus['syncJobStats'],
    dlqDepth: Number(dto.dlqDepth || 0),
    pullWatermarks: watermarksRaw.map((w: unknown) => {
      const wm = w as Record<string, unknown>;
      return {
        id: Number(wm.id),
        documentId: String(wm.documentId || ''),
        entityType: String(wm.entityType || ''),
        lastSyncedAt: String(wm.lastSyncedAt || ''),
        recordsSynced: Number(wm.recordsSynced || 0),
        createdAt: String(wm.createdAt || ''),
        updatedAt: String(wm.updatedAt || ''),
      } as SapPullWatermark;
    }),
  };
}
